import { useEffect, useState } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  checkEmailExists,
  confirmReset,
  onAuthStateChange,
  resetPassword,
  signIn,
  signOut,
  signUp,
  verifyResetCode,
} from '@chartai/firebase';

import { userKeys } from '../queryKeys';

interface AuthUser {
  uid: string;
  email: string | null;
  displayName: string | null;
}

export function useCurrentUser() {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChange((firebaseUser) => {
      if (firebaseUser) {
        setUser({
          uid: firebaseUser.uid,
          email: firebaseUser.email,
          displayName: firebaseUser.displayName,
        });
      } else {
        setUser(null);
      }
      setIsLoading(false);
    });

    return unsubscribe;
  }, []);

  return { user, isLoading };
}

export function useSignIn() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (credentials: { email: string; password: string }) => {
      return signIn(credentials.email, credentials.password);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: userKeys.current() });
    },
  });
}

export function useSignUp() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: { email: string; password: string }) => {
      return signUp(input.email, input.password);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: userKeys.current() });
    },
  });
}

export function useSignOut() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      await signOut();
    },
    onSuccess: () => {
      queryClient.clear();
    },
  });
}

export function useResetPassword() {
  return useMutation({
    mutationFn: async (email: string) => {
      await resetPassword(email);
    },
  });
}

export function useVerifyResetCode() {
  return useMutation({
    mutationFn: async (code: string) => {
      return verifyResetCode(code);
    },
  });
}

export function useConfirmPasswordReset() {
  return useMutation({
    mutationFn: async (input: { code: string; newPassword: string }) => {
      await confirmReset(input.code, input.newPassword);
    },
  });
}

export function useCheckEmailExists() {
  return useMutation({
    mutationFn: async (email: string) => {
      return checkEmailExists(email);
    },
  });
}
